import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Clock, Mail, ShieldX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SignupStatus, useSignupApi } from '../../common/hooks/use-signup-api.hook';
import { REVIEW_POLL_MS } from './activation';

/** The reasons a block can give; anything else reads as "other". */
export const BLOCK_REASONS = ['no_business', 'unrelated_business', 'duplicate', 'fake_details', 'other'] as const;

export const blockReasonKey = (reason?: string | null) => {
    const r = String(reason || '').toLowerCase();
    return (BLOCK_REASONS as readonly string[]).includes(r) ? r.toUpperCase() : 'OTHER';
};

/** Where a turned-down vendor can write; shown as text so it can be copied. */
export const CONTACT_EMAIL = 'hello@ardealy.com';

const DEFAULT_SLA_HOURS = 2;

/** What the person at Ardealy looks at, said up front so the wait is not a mystery. */
const CHECKS = ['REAL_STORE', 'PRODUCTS', 'SNIPPET'] as const;

/**
 * "We're checking your website" — both steps are done, and a person at
 * Ardealy approves each new account (docs/plg-review.md). The screen says
 * what is checked and how long it usually takes, asks the status every 30 s
 * while the tab is visible (approval swaps in the "You're connected" moment),
 * and offers a demo meanwhile.
 */
export const ReviewScreen = ({
    status,
    email,
    onStatus,
    onBookDemo,
}: {
    status: SignupStatus;
    email?: string;
    onStatus: (status: SignupStatus) => void;
    onBookDemo: () => void;
}) => {
    const { t } = useTranslation();
    const api = useSignupApi();
    const apiRef = useRef(api);
    apiRef.current = api;
    const onStatusRef = useRef(onStatus);
    onStatusRef.current = onStatus;

    useEffect(() => {
        const tick = async () => {
            if (document.visibilityState === 'hidden') return;
            const next = await Promise.resolve(apiRef.current.status()).catch(() => null);
            if (next) onStatusRef.current(next);
        };
        const id = window.setInterval(tick, REVIEW_POLL_MS);
        const onVisible = () => document.visibilityState === 'visible' && tick();
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    const hours = status.reviewSlaHours ?? DEFAULT_SLA_HOURS;
    const domain = status.domain || t('SIGNUP.GATE.YOUR_SITE');

    return (
        <div data-testid="gate-review">
            <span className="flex size-11 items-center justify-center rounded-full border text-muted-foreground">
                <Clock className="size-5" />
            </span>
            <h1 className="mt-5 font-heading text-[26px] leading-tight font-medium tracking-tight text-balance sm:text-[30px]">{t('SIGNUP.REVIEW.TITLE')}</h1>
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{t('SIGNUP.REVIEW.SUBTITLE', { domain })}</p>

            <section className="mt-6 rounded-xl border bg-card p-5">
                <h2 className="text-sm font-medium">{t('SIGNUP.REVIEW.WHAT_TITLE')}</h2>
                <ul className="mt-3 flex flex-col gap-2.5 text-sm">
                    {CHECKS.map((c) => (
                        <li key={c} className="flex gap-2.5">
                            <Check className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                            <span className="leading-relaxed">{t(`SIGNUP.REVIEW.CHECK_${c}`, { domain })}</span>
                        </li>
                    ))}
                </ul>
                <div className="mt-5 flex flex-col gap-2 border-t pt-4 text-sm text-muted-foreground">
                    <p className="flex items-center gap-2" data-testid="gate-review-sla">
                        <Clock className="size-4 shrink-0" />
                        {t('SIGNUP.REVIEW.SLA', { count: hours })}
                    </p>
                    <p className="flex items-center gap-2" data-testid="gate-review-email">
                        <Mail className="size-4 shrink-0" />
                        {email ? t('SIGNUP.REVIEW.EMAIL', { email }) : t('SIGNUP.REVIEW.EMAIL_NO_ADDRESS')}
                    </p>
                </div>
            </section>

            <section className="mt-3 rounded-xl border border-dashed p-5">
                <h2 className="text-sm font-medium">{t('SIGNUP.REVIEW.DEMO_TITLE')}</h2>
                {status.demoRequested ? (
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground" data-testid="gate-review-demo-requested">
                        {t('SIGNUP.REVIEW.DEMO_REQUESTED')}
                    </p>
                ) : (
                    <>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t('SIGNUP.REVIEW.DEMO_BODY')}</p>
                        <Button variant="outline" size="sm" className="mt-3" onClick={onBookDemo} data-testid="gate-review-demo">
                            {t('SIGNUP.DEMO.OPEN')}
                        </Button>
                    </>
                )}
            </section>
            <p className="mt-4 text-xs text-muted-foreground">{t('SIGNUP.REVIEW.AUTO')}</p>
        </div>
    );
};

/**
 * "We couldn't approve this account": the reason in plain words, the admin's
 * own message when there is one, where to write, and a demo as the way on.
 */
export const BlockedScreen = ({ status, onBookDemo }: { status: SignupStatus; onBookDemo: () => void }) => {
    const { t } = useTranslation();
    const message = status.review?.message?.trim();
    return (
        <div data-testid="gate-blocked">
            <span className="flex size-11 items-center justify-center rounded-full border border-destructive/40 text-destructive">
                <ShieldX className="size-5" />
            </span>
            <h1 className="mt-5 font-heading text-[26px] leading-tight font-medium tracking-tight text-balance sm:text-[30px]">{t('SIGNUP.BLOCKED.TITLE')}</h1>
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground" data-testid="gate-blocked-reason">
                {t(`SIGNUP.BLOCKED.REASON.${blockReasonKey(status.review?.reason)}`, { domain: status.domain || t('SIGNUP.GATE.YOUR_SITE') })}
            </p>

            {message && (
                <figure className="mt-6 rounded-xl border bg-card p-5" data-testid="gate-blocked-message">
                    <figcaption className="text-xs font-medium text-muted-foreground">{t('SIGNUP.BLOCKED.MESSAGE_LABEL')}</figcaption>
                    <blockquote className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">{message}</blockquote>
                </figure>
            )}

            <section className="mt-3 rounded-xl border bg-card p-5">
                <h2 className="text-sm font-medium">{t('SIGNUP.BLOCKED.NEXT_TITLE')}</h2>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {t('SIGNUP.BLOCKED.REPLY')}{' '}
                    <span dir="ltr" className="font-medium text-foreground select-all" data-testid="gate-blocked-contact">
                        {CONTACT_EMAIL}
                    </span>
                </p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t('SIGNUP.BLOCKED.DEMO_BODY')}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={onBookDemo} data-testid="gate-blocked-demo">
                    {t('SIGNUP.DEMO.OPEN')}
                </Button>
            </section>
        </div>
    );
};
